// The desktop game's side panels: today's edition and the rank ladder on the left, a live
// scorecard on the right, and in a challenge the score to beat, on the right and on the ladder.
// Hidden on phones, where the board itself shows row scores and a bar shows the score to beat.
import { Fragment } from 'react'
import type { EditionInfo, Play } from '@wbwl/contract'
import { scoreWord } from '@wbwl/rules'
import { fmt, goldByTier } from '../lib/game.ts'
import { chasePct, type Target } from './challenge-ui.tsx'
import { Tile, WordTiles } from './game-ui.tsx'
import { Icon } from './Icon.tsx'
import { Avatar } from './shell.tsx'

export function EditionPanel({ info, total, target }: { info: EditionInfo; total: number; target: Target | null }) {
  const current = info.ranks.reduce((idx, r, i) => (total >= r.at ? i : idx), 0)
  // The score to beat sits on the ladder under the rank it reached.
  const targetAt = target ? info.ranks.reduce((idx, r, i) => (target.total >= r.at ? i : idx), 0) : -1
  return (
    <aside className="side side-left">
      <div className="panel">
        <div className="double-rule" />
        <div className="eyebrow dateline"><span>No. {info.number}</span><span>{info.date}</span></div>
        <div className="rule ink" />
        <div className="h-serif side-headline">{info.headline}</div>
        <div className="tiers">
          {goldByTier(info.gold).map(([tier, letters]) => (
            <div className="tier" key={tier}>
              <span className="tl">+{tier}</span>
              <div className="ts">{letters.map((l) => <Tile key={l} l={l} k="played gold" size={28} gb={false} pt={false} gold={info.gold} />)}</div>
            </div>
          ))}
        </div>
        <p className="muted small">{info.burns.length} burn tiles. A letter placed on one is gone for the rest of the day and drops into your ashes.</p>
      </div>
      <div className="panel">
        <div className="panel-head"><b>Ranks today</b><span className="eyebrow">best found {fmt(info.best)}</span></div>
        <div className="ladv">
          {info.ranks.map((r, i) => (
            <Fragment key={r.name}>
              <div className={i < current ? 'done' : i === current ? 'cur' : ''}>
                <i /><span className="n">{r.name}</span><span className="v">{fmt(r.at)}{i === current ? ' · you' : ''}</span>
              </div>
              {target && i === targetAt && (
                <div className="target"><i /><span className="n ellipsis">{target.name} · to beat</span><span className="v">{fmt(target.total)}</span></div>
              )}
            </Fragment>
          ))}
        </div>
      </div>
    </aside>
  )
}

/** Whose score you're chasing in a challenge, and how far off it you are. */
function ChallengePanel({ target, total }: { target: Target; total: number }) {
  const gap = total - target.total
  return (
    <div className={`panel ch-side${gap > 0 ? ' ahead' : ''}`}>
      <div className="panel-head"><b>Challenge</b><span className="eyebrow">score to beat</span></div>
      <div className="ch-side-who">
        <Avatar id={target.id} name={target.name} src={target.avatar} size={40} />
        <div>
          <span className="muted small ellipsis">{target.name}{target.rankName ? ' made the' : ''}</span>
          {target.rankName && <span className="ch-side-rank">{target.rankName}</span>}
        </div>
        <b className="ch-side-total">{fmt(target.total)}</b>
      </div>
      <div className="vs-track"><i style={{ width: chasePct(total, target) }} /></div>
      <p className="ch-side-to">
        {gap > 0 ? <>You’re ahead of {target.name} by <b>{fmt(gap)}</b>.</>
          : gap === 0 ? <>Level with {target.name}. One more point wins it.</>
          : <>You need <b>{fmt(1 - gap)}</b> more to beat {target.name}.</>}
      </p>
    </div>
  )
}

export function ScorecardPanel({ info, play, typing, valid, target }: { info: EditionInfo; play: Play; typing: string; valid: boolean; target: Target | null }) {
  const live = play.phase === 'play' && typing.length === 5 && valid ? scoreWord(typing, info) : null
  const rows = []
  for (let r = 0; r < 6; r++) {
    const w = play.rows[r]
    if (w) {
      const s = play.scores.rows[r]
      rows.push(<div className="scr" key={r}><span className="n">{r + 1}</span><WordTiles edition={info} word={w} row={r} size={22} /><span className="m">{s.pts}×{s.mult}</span><span className="s">{s.total}</span></div>)
    } else if (r === play.rows.length && play.phase === 'play') {
      rows.push(<div className="scr live" key={r}><span className="n on">▸</span>{typing ? <WordTiles edition={info} word={typing} row={r} size={22} k="typed" /> : <span className="empty">typing…</span>}<span className="m">{live ? `${live.pts}×${live.mult}` : ''}</span><span className="s faint">{live ? live.total : ''}</span></div>)
    } else {
      rows.push(<div className="scr" key={r}><span className="n">{r + 1}</span><span className="empty">—</span><span /><span className="s" /></div>)
    }
  }
  return (
    <aside className="side side-right">
      {target && <ChallengePanel target={target} total={play.phase === 'phoenix' ? play.scores.rowsTotal : play.scores.total} />}
      <div className="panel">
        <div className="panel-head"><b>Scorecard</b><span className="eyebrow">pts × mult</span></div>
        {rows}
        <div className="scr phx-row">
          <span className="n burn-ink"><Icon name="feather" size={15} /></span>
          {play.phoenix ? <WordTiles edition={info} word={play.phoenix} size={22} k="ash" /> : <span className="empty">Phoenix · after row 6</span>}
          <span className="m">{play.scores.phoenix ? `${play.scores.phoenix.pts}×${play.scores.phoenix.mult}` : ''}</span>
          <span className="s">{play.scores.phoenix?.total ?? ''}</span>
        </div>
        <div className="total-row"><span className="eyebrow ink">Total</span><span className="total-num">{fmt(play.scores.total)}</span></div>
      </div>
      <div className="panel ash-note">
        <div className="ash-note-head"><Icon name="flame" size={16} /><b>Your ashes so far</b></div>
        <p className="muted small">{play.ashes.length ? `${play.ashes.join(' ')} — ` : 'Nothing has burned yet. '}{ashHint(play.ashes)}</p>
      </div>
      <div className="kbd-hints faint">
        <span className="kbd">A–Z</span><span>type</span><span className="kbd">⏎</span><span>play word</span><span className="kbd">⌫</span><span>delete</span><span className="kbd">?</span><span>rules</span>
      </div>
    </aside>
  )
}

function ashHint(ashes: readonly string[]): string {
  const vowels = ashes.filter((l) => 'AEIOU'.includes(l)).length
  if (!ashes.length) return 'Burned letters power your phoenix at the end.'
  if (!vowels) return 'No vowels yet. Your phoenix will need one, but every vowel you burn is one you can’t play.'
  return `${vowels} vowel${vowels === 1 ? '' : 's'} in there, so a phoenix is likely.`
}
